/**
 * Edit Billing Identity Modal
 *
 * Modal for editing an existing billing identity's details.
 */

import { useEffect, useState } from 'react';
import {
  type BillingEntityAddressFormData,
  BillingEntityForm,
  type BillingEntityFormData,
  type PayToToggleSource,
  useBillingEntityOperations,
  useBillingEntityOperationsAdmin,
  validatePayToAddress,
} from '@features/billing-entities';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { BillingEntityResponse } from '@store/generated/billing-entities';
import { useSnackbar } from 'notistack';

import { extractApiErrorMessage } from '../utils/errorUtils';

interface EditBillingEntityModalProps {
  open: boolean;
  onClose: () => void;
  entity: BillingEntityResponse;
  onUpdated: () => void;
  /** When provided, uses admin API routes (for admin panel context) */
  organizationId?: string;
}

function entityToFormData(entity: BillingEntityResponse): BillingEntityFormData {
  const payToAddress = entity.pay_to_address;
  return {
    billing_npi: entity.billing_npi || '',
    tax_id: '', // Tax ID is masked, user needs to re-enter if changing
    tax_id_type: entity.tax_id_type || 'EIN',
    taxonomy_code: entity.taxonomy_code || '',
    legal_name: entity.legal_name || '',
    doing_business_as: entity.doing_business_as || '',
    billing_address: {
      line1: entity.billing_address?.line1 || '',
      line2: entity.billing_address?.line2 || '',
      city: entity.billing_address?.city || '',
      state: entity.billing_address?.state || '',
      zip: entity.billing_address?.zip || '',
    },
    pay_to_address: {
      line1: payToAddress?.line1 || '',
      line2: payToAddress?.line2 || '',
      city: payToAddress?.city || '',
      state: payToAddress?.state || '',
      zip: payToAddress?.zip || '',
    },
    has_pay_to_address: !!payToAddress,
    contact_name: entity.contact_name || '',
    contact_email: entity.contact_email || '',
    contact_phone: entity.contact_phone || '',
  };
}

export function EditBillingEntityModal({
  open,
  onClose,
  entity,
  onUpdated,
  organizationId,
}: EditBillingEntityModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { billing } = useEventTracking();
  const providerOps = useBillingEntityOperations();
  const adminOps = useBillingEntityOperationsAdmin();
  const { updateLoading } = organizationId ? adminOps : providerOps;
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Per-field errors: client-side pay-to validation plus the backend's PO Box
  // rejection, which needs to point at a field rather than float in the Alert.
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});

  const [formData, setFormData] = useState<BillingEntityFormData>(() => entityToFormData(entity));

  // Reset form when entity changes
  useEffect(() => {
    setFormData(entityToFormData(entity));
    setSubmitError(null);
    setFieldErrors({});
  }, [entity]);

  const handleChange = (field: keyof BillingEntityFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field: keyof BillingEntityAddressFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      billing_address: { ...prev.billing_address, [field]: value },
    }));
  };

  const handlePayToAddressChange = (field: keyof BillingEntityAddressFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      pay_to_address: { ...prev.pay_to_address, [field]: value },
    }));
    // Clear the matching per-field error as the user corrects it.
    setFieldErrors(prev => {
      const key = `pay_to_address.${field}`;
      if (!prev[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleTogglePayTo = (checked: boolean, source: PayToToggleSource) => {
    setFormData(prev => ({ ...prev, has_pay_to_address: checked }));
    if (!checked) {
      // Drop any stale pay-to validation errors along with the fields.
      setFieldErrors(prev =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith('pay_to_address.'))),
      );
    }
    // admin-v2 surfaces are exempt from product analytics; organizationId is
    // this modal's admin discriminator.
    if (!organizationId) {
      billing.payToAddressToggled({ enabled: checked, source, context: 'edit_modal' });
      if (source === 'po_box_warning') {
        billing.poBoxWarningActionClicked({ context: 'edit_modal' });
      }
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setFieldErrors({});

    // Pay-to fields are validated before the request is built. The Save button
    // is a plain onClick (no <form>), so the inputs' `required` props never gate
    // anything — without this an incomplete or malformed pay-to address would
    // reach the API, and an all-blank one would silently save as no pay-to.
    if (formData.has_pay_to_address) {
      const payToErrors = validatePayToAddress(formData.pay_to_address);
      if (Object.keys(payToErrors).length > 0) {
        setFieldErrors(payToErrors);
        setSubmitError('Please complete the pay-to address before saving.');
        return;
      }
    }

    // Build update request
    const updateRequest: Record<string, unknown> = {
      taxonomy_code: formData.taxonomy_code.trim() || null,
      legal_name: formData.legal_name.trim(),
      doing_business_as: formData.doing_business_as.trim() || null,
      billing_address: {
        line1: formData.billing_address.line1.trim(),
        line2: formData.billing_address.line2.trim() || null,
        city: formData.billing_address.city.trim(),
        state: formData.billing_address.state.trim().toUpperCase(),
        zip: formData.billing_address.zip.trim(),
      },
      contact_name: formData.contact_name.trim() || null,
      contact_email: formData.contact_email.trim(),
      contact_phone: formData.contact_phone.trim(),
    };

    // Pay-to address (2010AB). Omitting the key leaves the stored value
    // untouched, so an explicit clear flag is needed when the provider unchecks
    // a pay-to address the entity already had. Sending both is a 400.
    const hadPayToAddress = !!entity.pay_to_address;
    if (formData.has_pay_to_address) {
      updateRequest.pay_to_address = {
        line1: formData.pay_to_address.line1.trim(),
        line2: formData.pay_to_address.line2.trim() || null,
        city: formData.pay_to_address.city.trim(),
        state: formData.pay_to_address.state.trim().toUpperCase(),
        zip: formData.pay_to_address.zip.trim(),
      };
    } else if (hadPayToAddress && !formData.has_pay_to_address) {
      updateRequest.clear_pay_to_address = true;
    }

    // Only include NPI and tax fields if entity is NOT synced
    if (!entity.stedi_synced) {
      updateRequest.billing_npi = formData.billing_npi.replace(/\D/g, '');

      // Only include tax_id if user entered a new one
      if (formData.tax_id.trim()) {
        updateRequest.tax_id = formData.tax_id.replace(/\D/g, '');
        updateRequest.tax_id_type = formData.tax_id_type;
      }
    }

    try {
      if (organizationId) {
        await adminOps.update({
          organizationId,
          billingEntityId: entity.billing_entity_id,
          updateBillingEntityRequest: updateRequest,
        }).unwrap();
      } else {
        await providerOps.update({
          billingEntityId: entity.billing_entity_id,
          updateBillingEntityRequest: updateRequest,
        }).unwrap();
      }

      enqueueSnackbar('Changes saved. You may need to verify your billing identity again.', {
        variant: 'success',
      });
      onUpdated();
      onClose();
    } catch (err) {
      const errorMessage = extractApiErrorMessage(
        err,
        'Failed to update billing identity. Please check your information and try again.',
      );
      setSubmitError(errorMessage);
      // The backend rejects PO Box / PMB style billing addresses with a
      // validation_error naming "physical street address" — mark the address
      // fields so the Alert has somewhere to point.
      if (/physical street address/i.test(errorMessage)) {
        setFieldErrors({ billing_address: errorMessage });
      }
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"md"}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Edit Billing Identity</span>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {entity.stedi_synced && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This identity has been verified. NPI and Tax ID cannot be changed after verification. To
            use a different NPI or Tax ID, create a new billing identity.
          </Alert>
        )}

        <BillingEntityForm
          data={entity.stedi_synced ? { ...formData, tax_id: entity.tax_id_masked || '' } : formData}
          onChange={handleChange}
          onAddressChange={handleAddressChange}
          onPayToAddressChange={handlePayToAddressChange}
          onTogglePayTo={handleTogglePayTo}
          errors={fieldErrors}
          disabledFields={
            entity.stedi_synced
              ? { billing_npi: true, tax_id: true, tax_id_type: true }
              : undefined
          }
          taxIdRequired={false}
          taxIdHelperText={
            entity.stedi_synced
              ? undefined
              : 'Only enter if you need to change the Tax ID'
          }
          taxIdPlaceholder={
            entity.stedi_synced
              ? undefined
              : `Current: ${entity.tax_id_masked} (leave blank to keep)`
          }
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={updateLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updateLoading}
          startIcon={updateLoading ? <CircularProgress size={20} /> : undefined}
        >
          {updateLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
